import { useEffect, useReducer, useRef, useState } from 'react';
import { useApi } from '../api/api-context';
import { ERROR_CODES, isApiError } from '../api/client';
import type { Repertoire, ReviewItem, UciMove } from '../api/types';
import type { BoardArrow } from '../board/chessground';
import { sanLabel } from '../board/notation';
import {
  currentCard,
  isTeaching,
  numberedMoves,
  sessionProgress,
  sessionReducer,
  startSession,
  type CardKind,
  type SessionState,
  type SessionStats,
} from '../state/review-session';
import { BoardView } from './BoardView';

export interface ReviewTrainerProps {
  repertoireId: string;
  side: Repertoire['side'];
  items: ReviewItem[];
  onDone: (stats: SessionStats, reviewed: number) => void;
  onExit: () => void;
}

const STEP_MS = 420;
const FIRST_STEP_MS = 250;
/** A quick test starts with a pause, so the rewind reads as a new round. */
const CHECK_INTRO_MS = 900;
const ROUND_LABELS: Record<CardKind, string> = { drill: 'Move ', check: 'Quick test · ', redrill: 'Mistakes · ' };
const AUTO_ADVANCE_MS = 750;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function turnOf(fen: string): 'w' | 'b' {
  return fen.split(/\s+/)[1] === 'b' ? 'b' : 'w';
}

function arrowsFor(state: SessionState, item: ReviewItem): BoardArrow[] {
  const arrows: BoardArrow[] = [];
  if (state.phase === 'retry' && state.attempt) {
    arrows.push({ uci: state.attempt.uci, brush: 'red' });
    arrows.push({ uci: item.uci as UciMove, brush: 'green' });
  } else if (state.phase === 'prompt') {
    if (state.attempt?.verdict === 'sibling') arrows.push({ uci: state.attempt.uci, brush: 'blue' });
    if (state.revealed) arrows.push({ uci: item.uci as UciMove, brush: 'green' });
  }
  return arrows;
}

/**
 * Line-walking spaced-repetition trainer (spec v1.7). ItsAllPrep plays the moves
 * that lead to each of your repertoire moves; you play yours on the board. Grading
 * is automatic, mistakes show the right move and must be played, and missed moves
 * come back once at the end of the session. New moves are taught with an arrow,
 * then played again without one in a quick test before the session leaves the line.
 */
export function ReviewTrainer({ repertoireId, side, items, onDone, onExit }: ReviewTrainerProps): JSX.Element {
  const api = useApi();
  const [state, dispatch] = useReducer(sessionReducer, items, startSession);
  const [saveError, setSaveError] = useState<string | null>(null);
  const posted = useRef(new Set<number>());
  const promptedAt = useRef<number>(Date.now());
  const introduced = useRef(-1);
  const card = currentCard(state);
  const item = card?.item ?? null;
  const progress = sessionProgress(state);
  const reduced = prefersReducedMotion();

  // Report each first-attempt outcome once. The server schedules; we only tell it.
  useEffect(() => {
    const record = state.record;
    if (record === null || posted.current.has(record.seq)) return;
    posted.current.add(record.seq);
    const latencyMs = Math.min(Math.max(Date.now() - promptedAt.current, 0), 3_600_000);
    const key = crypto.randomUUID();
    const body = { edgeId: record.edgeId, grade: record.grade, mode: record.mode, latencyMs };
    void api.gradeReview(repertoireId, body, key).catch(async (error: unknown) => {
      // The move was removed or disabled while reviewing: nothing to schedule.
      if (isApiError(error, ERROR_CODES.SRS_ITEM_NOT_SCHEDULED) || isApiError(error, 'RESOURCE_NOT_FOUND')) return;
      try {
        await api.gradeReview(repertoireId, body, key);
      } catch {
        setSaveError('Some answers could not be saved. Check your connection; the session continues.');
      }
    });
  }, [api, repertoireId, state.record]);

  // Play the moves leading to the cue, one at a time.
  useEffect(() => {
    if (state.phase !== 'context') return undefined;
    const first = item !== null && state.pending.length === item.path.length;
    const intro = progress.kind === 'check' && progress.position === 1 && introduced.current !== state.index;
    const timer = window.setTimeout(
      () => {
        introduced.current = state.index;
        dispatch({ type: reduced ? 'skip-context' : 'step' });
      },
      reduced ? 0 : intro ? CHECK_INTRO_MS : first ? FIRST_STEP_MS : STEP_MS,
    );
    return () => window.clearTimeout(timer);
  }, [state.phase, state.pending, state.index, item, progress.kind, progress.position, reduced]);

  useEffect(() => {
    if (state.phase === 'prompt' && state.attempt === null) promptedAt.current = Date.now();
  }, [state.phase, state.attempt, state.index]);

  // Clean first-try answers move on by themselves; misses and ideas wait for the learner.
  // A quick test skips the idea: it was on screen moments ago.
  const showsIdea = item !== null && Boolean(item.comment) && progress.kind !== 'check';
  const waitsForContinue = state.phase === 'solved' && item !== null &&
    (state.grade !== 'correct' || showsIdea);
  useEffect(() => {
    if (state.phase !== 'solved' || waitsForContinue) return undefined;
    const timer = window.setTimeout(() => dispatch({ type: 'advance' }), reduced ? 250 : AUTO_ADVANCE_MS);
    return () => window.clearTimeout(timer);
  }, [state.phase, waitsForContinue, reduced, state.index]);

  useEffect(() => {
    if (state.phase === 'done') onDone(state.stats, state.total);
  }, [state.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 'h') dispatch({ type: 'reveal' });
      else if (key === 's') dispatch({ type: 'skip' });
      else if (key === 'n' || key === 'arrowright') dispatch({ type: 'advance' });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (card === null || item === null || state.phase === 'done') {
    return <p role="status">Session complete.</p>;
  }

  const { kind, position, count, done } = progress;
  const total = state.total;
  const teaching = isTeaching(card);
  const orientation = side === 'black' ? 'b' : side === 'white' ? 'w' : item.sideToMove;
  const turn = turnOf(state.board.fen);
  const interactive = state.phase === 'prompt' || state.phase === 'retry';
  const played = item.path.length - state.pending.length;
  const lineSans = [
    ...item.path.slice(0, played).map((step) => step.san),
    ...(state.phase === 'solved' ? [item.san] : state.phase === 'context' ? [] : ['?']),
  ];
  const attemptSan = state.attempt ? sanLabel(item.fen, state.attempt.uci) : '';
  const siblingCount = item.siblings.length;

  function onBoardMove(uci: string): boolean {
    const correct = uci === item!.uci;
    dispatch({ type: 'move', uci: uci as UciMove });
    return correct;
  }

  let message: JSX.Element;
  if (state.phase === 'context') {
    message = kind === 'check' ? (
      <p className="trainer-message">
        <strong>Quick test.</strong> The line again, this time without arrows…
      </p>
    ) : (
      <p className="trainer-message">Playing the line…</p>
    );
  } else if (state.phase === 'retry') {
    message = (
      <p className="trainer-message trainer-miss">
        <strong>Not quite.</strong> You played <span className="san">{attemptSan}</span>; your repertoire
        plays <span className="san">{item.san}</span>. Play it on the board to continue.
      </p>
    );
  } else if (state.phase === 'solved') {
    message = (
      <p className={`trainer-message ${state.grade === 'correct' ? 'trainer-hit' : 'trainer-miss'}`}>
        <strong>{state.grade === 'correct' ? '✓ Correct' : state.grade === 'alternative' ? '✓ Main move found' : '✓ Played'}</strong>{' '}
        <span className="san">{item.san}</span>
        {state.grade === 'incorrect' && kind !== 'redrill' && ' — this move comes back at the end of the session.'}
        {state.grade === 'alternative' && kind === 'drill' && ' — you reached for an alternative first, so it returns a little sooner.'}
      </p>
    );
  } else if (state.attempt?.verdict === 'sibling') {
    message = (
      <p className="trainer-message">
        <span className="san">{attemptSan}</span> is also in your repertoire, but this card drills a
        different move here. Try again.
      </p>
    );
  } else if (state.revealed && teaching) {
    message = (
      <p className="trainer-message">
        <strong>New move.</strong> Play <span className="san">{item.san}</span>, shown by the green arrow.
      </p>
    );
  } else if (state.revealed) {
    message = <p className="trainer-message">Play <span className="san">{item.san}</span>, shown by the green arrow.</p>;
  } else {
    message = (
      <p className="trainer-message">
        <strong>{kind === 'check' ? 'Quick test.' : 'Your move.'}</strong> What do you play here?
        {siblingCount > 0 && ` You have ${siblingCount + 1} moves here; this card asks for one of them.`}
      </p>
    );
  }

  return (
    <section className="trainer" aria-labelledby="trainer-title">
      <div className="trainer-progress">
        <h2 id="trainer-title" className="sr-only">Review session</h2>
        <span>{`${ROUND_LABELS[kind]}${position} of ${count}`}</span>
        <div className="trainer-bar" role="progressbar" aria-label="Session progress" aria-valuemin={0}
          aria-valuemax={total} aria-valuenow={done}>
          <span style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
        </div>
        <span className="trainer-tally" aria-label={`${state.stats.correct} correct, ${state.stats.incorrect} missed`}>
          <span className="tally-hit">✓ {state.stats.correct + state.stats.alternative}</span>
          <span className="tally-miss">✗ {state.stats.incorrect}</span>
        </span>
      </div>
      <div className="trainer-layout">
        <div className="trainer-board">
          <BoardView
            input={{
              fen: state.board.fen,
              turnColor: turn,
              orientation,
              dests: interactive ? item.legalMoves : [],
              lastMove: state.board.lastMove,
              arrows: arrowsFor(state, item),
            }}
            onMove={onBoardMove}
            label={`Review chessboard, ${turn === 'w' ? 'white' : 'black'} to move`}
          />
        </div>
        <div className="trainer-side">
          <div role="status" aria-live="polite">{message}</div>
          {state.phase === 'solved' && showsIdea && (
            <p className="review-idea"><strong>Idea:</strong> {item.comment}</p>
          )}
          {saveError && <p role="alert" className="error">{saveError}</p>}
          <div className="trainer-line" aria-label="Line so far">
            {lineSans.length === 0 ? (
              <span className="muted">Starting position</span>
            ) : (
              numberedMoves(item.startFen, lineSans).map((move, index) => (
                <span key={index} className={index === lineSans.length - 1 && state.phase !== 'context' ? 'trainer-current' : undefined}>
                  {move.label && <span className="move-number">{move.label}</span>}
                  <span className="san">{move.san}</span>
                </span>
              ))
            )}
          </div>
          <div className="trainer-actions">
            {state.phase === 'solved' && waitsForContinue && (
              // The only action left, so Enter continues without hunting for the button.
              <button type="button" className="studio-primary" autoFocus onClick={() => dispatch({ type: 'advance' })}>
                Continue <kbd>N</kbd>
              </button>
            )}
            {(state.phase === 'prompt' || state.phase === 'context') && !state.revealed && (
              <button type="button" className="secondary" onClick={() => dispatch({ type: 'reveal' })}>
                Show move <kbd>H</kbd>
              </button>
            )}
            {state.phase !== 'solved' && (
              <button type="button" className="secondary" onClick={() => dispatch({ type: 'skip' })}>
                Skip <kbd>S</kbd>
              </button>
            )}
            <button type="button" className="secondary" onClick={onExit}>End session</button>
          </div>
          <p className="hint">
            ItsAllPrep plays the moves that lead here; you play yours. Showing the move counts as a miss.
          </p>
        </div>
      </div>
    </section>
  );
}
